/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle,
  faExclamationCircle,
  faWarning,
} from '@fortawesome/free-solid-svg-icons'
import { selectModal, setModal } from '../../redux/modalSlice'
import { setModuleData } from "../../redux/moduleDataSlice"
import { selectDropdowns, selectDropdownOptions } from '../../redux/dropdownSlice'
import { setNotification } from '../../redux/notificationSlice'
import { useRequest } from '../../hooks/UseRequest'
import { generateErrorBody } from '../../helpers/errorHandling'
import { selectPortfolios } from '../../redux/portfolioSlice'
import Loading from '../../components/Loading'
import CodeMirror from '@uiw/react-codemirror'
import { formatNoteBody } from '../../helpers/formatting'
// TODO: Would be nice to get a better looking html but I can't seem to get this installed.
// https://github.com/uiwjs/react-codemirror
// import { html } from '@codemirror/lang-html'

function Notification({ data, setError }) {
  const dispatch = useDispatch()
  const [messageIcon, setMessageIcon] = useState(faCheckCircle)

  useEffect(() => {
    switch (data.type) {
      case 'success':
        setMessageIcon(faCheckCircle)
        break
      case 'error':
        setMessageIcon(faExclamationCircle)
        break
      case 'warning':
        setMessageIcon(faWarning)
        break
    }
  }, [data.type])

  if (data.type) {
    return (
      <span className="lox-notification-banner">
        <div
          className={`notification ${data.type}_message lox-slide-up-notification mb-3`}
        >
          <h3 className="title is-size-5 mb-1 is-capitalized">
            <FontAwesomeIcon icon={messageIcon} className="mr-2" />
            {data.type}
          </h3>
          <p className="ml-5 is-size-6 is-flex is-justify-content-space-between">
            {data.body}
            {data.type === 'warning' && (
              <div className="lox-form buttons-wrapper">
                <button
                  className="button has-background-grey has-text-white is-small px-3"
                  onClick={() => {
                    setError({ type: '', body: '' })
                    dispatch(setModal({ title: '', content: '', type: '' }))
                  }}
                >
                  YES
                </button>
                <button
                  className="button has-background-grey has-text-white is-small px-3 ml-5"
                  onClick={() => {
                    setError({ type: '', body: '' })
                  }}
                >
                  NO
                </button>
              </div>
            )}
          </p>
        </div>
      </span>
    )
  } else {
    return ''
  }
}

export default function CodeEditorModal() {
  const dispatch = useDispatch()
  const request = useRequest()
  const modal = useSelector(selectModal)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const portfolios = useSelector(selectPortfolios);
  // Local State
  const [error, setError] = useState({ type: '', body: '' })
  const [placeholders, setPlaceholders] = useState([])
  const [saving, setSaving] = useState(false)
  const requesting = useRef(false)
  const dropDowns = useSelector(selectDropdowns)
  let codeEditor = useRef();
  const [val, setVal] = useState(modal.content);
  const [s3Body, setS3Body] = useState(null);

  const closeModal = (e) => {
    e.preventDefault()
    if (modal.type === 'html_view' || modal.type === 'html_email_body') {
      dispatch(setModal({ title: '', content: '', type: '' }))
      setS3Body(null)
    } else {
      const formData = val
      if (formData === modal.content) {
        setError({ type: '', body: '' })
        dispatch(setModal({ title: '', content: '', type: '' }))
      } else {
        setError({
          type: 'warning',
          body: 'You have unsaved changes. Are you sure you want to exit?',
        })
      }
    }
  }

  const templatePlaceholders = () => {
    let content = modal.content
    // Format customer notes
    if (modal.options.module_code_key === 'customer_note') {
      content = formatNoteBody(modal.options.record)
    }
    if (modal.options.record.portfolio_id) {
      const portfolio = portfolios.data.find((x) => x.id === modal.options.record.portfolio_id)
      content = content.replaceAll('{{$template_placeholder_aws_bucket_name}}', portfolio.aws_bucket_name).replaceAll('{{$template_placeholder_app_url}}', portfolio.app_url)
    }
    return content
  }

  const loadS3Body = async () => {
    if (modal.type === 'html_email_body' && modal.content?.s3_link != '') {
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer_email_message/${modal.content?.id}/s3_body`)
      try {
        const response = await request.get()
        if (response.status === 200 || response.status === 201) {
          if (response.data?.data?.body) {
            setS3Body(response.data.data.body)
          } else {
            setS3Body('')
          }
        } else {
          dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
        dispatch(setNotification({ type: 'error', body: errorMessage }))
      }
    }
  }

  useEffect(() => {
    if (
      modal.type === 'html_edit_placeholder'
      && placeholders.length === 0
      && !requesting.current
    ) {
      const dropdownId = dropDowns?.data.find((e) => e.code_key === 'template_placeholder')?.id
      setPlaceholders(dropdownOptions?.filter((o) => (o.is_active === 1 && o.dropdown_id === dropdownId)))
    } else if (
      modal.type === 'html_email_body'
      && placeholders.length === 0
      && !requesting.current
    ) {
        loadS3Body();
    }
    setVal(modal.content)
  }, [modal.type])

  const onChange = useCallback((value, viewUpdate) => {
    setVal(value)
  }, []);

  const codeMirrorSetup = () => {
    return (
      <CodeMirror
        value={val}
        // extensions={[html()]}
        ref={(ref) => (codeEditor = ref)}
        autoFocus={1}
        height="75vh"
        theme="dark"
        onChange={onChange}
      />
    )
  }

  const insertPlaceholder = (placeholder) => {
    const value = `{{$${placeholder}}}`
    const state = codeEditor.view.viewState.state;
    const range = state.selection.ranges[0];
    codeEditor.view.dispatch({
      changes: {
        from: range.from,
        to: range.to,
        insert: value
      },
      selection: { anchor: range.from + value.length }
    })
    codeEditor.view.focus()
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    const formData = val
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/${modal.options.module_code_key}/${modal.options.record.id}`)

    try {
      let record = { ...modal.options.record }
      record[modal.options.column_name] = formData

      // If the modal is a customer note, we need to convert the tags to an array of ids
      if (modal?.options?.module_code_key === 'customer_note') {
        record['tags'] = record['tags']?.map((e) => e.id)
      }

      const response = await request.edit(record)
      if (response.status === 200 || response.status === 201) {
        setSaving(false)
        dispatch(setModuleData({ data: [response.data.data] }))
        dispatch(setNotification({ type: '', body: '' }))
        dispatch(setModal({ title: '', content: '', type: '' }))
      } else {
        setError({ type: 'error', body: `Error: ${response.status}` })
      }
    } catch (error) {
      let errorMessage = ''
      switch (error?.response?.status) {
        case 403:
          errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
          break
        case 422:
          errorMessage = generateErrorBody(error.response?.data?.errors)
          break
        default:
          errorMessage = 'There was an error submitting'
      }
      setError({ type: 'error', body: errorMessage })
      setSaving(false)
    }
  }

  if (
    modal.type === 'html_edit'
    || modal.type === 'html_view'
    || modal.type === 'html_edit_placeholder'
    || modal.type === 'html_email_body'
  ) {
    return (
      <div className="modal is-active is-large">
        <div className="modal-background" onClick={closeModal}></div>
        <div className="modal-card column is-four-fifths">
          <form id="code_form" onSubmit={onSubmit}>
            <header className="modal-card-head py-3">
              <p className="modal-card-title is-capitalized">
                HTML Viewer: {modal.title}
              </p>
              <button
                className="delete"
                aria-label="close"
                onClick={closeModal}
              ></button>
            </header>
            {modal.type === 'html_edit_placeholder' ? (
              <section className="modal-card-body lox-footer-modal-card-body">
                {error.type && (
                  <Notification data={error} setError={setError} />
                )}
                <div className="columns">
                  <div className="column is-three-quarters is-clipped">
                    {codeMirrorSetup()}
                  </div>
                  <div className="column is-one-quarter" style={{ maxHeight: '800px', overflow: 'scroll' }}>
                    {placeholders.map((e, i) => {
                      return (
                        <button
                          // eslint-disable-next-line react/no-array-index-key
                          key={i}
                          className="button is-small has-background-grey has-text-white is-fullwidth mb-1"
                          type="button"
                          onClick={() => {
                            insertPlaceholder(e.code_key)
                          }}
                        >
                          {e.name}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </section>
            ) : modal.type === 'html_email_body' ? (
              <section className="modal-card-body lox-footer-modal-card-body">
                {s3Body == null && (
                  <Loading />
                )}
                <div className="columns">
                  <div className="column is-full is-clipped">
                    {s3Body != '' && (
                      <iframe title="template" srcDoc={s3Body} style={{ display: 'block', width: '100%', height: '75vh' }} />
                    )}
                    {s3Body == '' && (
                      <p>No Content Found</p>
                    )}
                  </div>
                </div>
              </section>
            ) : (
              <section className="modal-card-body lox-footer-modal-card-body">
                {error.type && (
                  <Notification data={error} setError={setError} />
                )}
                <div className="columns">
                  <div className="column is-full is-clipped">
                    {modal.type === 'html_view' && (
                      <iframe title="template" srcDoc={templatePlaceholders()} style={{ display: 'block', width: '100%', height: '70vh' }} />
                    )}
                    {modal.type === 'html_edit' && (
                      <>
                        {codeMirrorSetup()}
                      </>
                    )}
                  </div>
                </div>
              </section>
            )}
            <footer className="modal-card-foot is-justify-content-end py-1">
              {modal.type === 'html_view' || modal.type === 'html_email_body' ? (
                <button className="button is-primary" onClick={closeModal}>
                  Close
                </button>
              ) : (
                <div className="lox-form__buttons-wrapper has-text-right">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="button is-small has-background-grey has-text-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="code_form"
                    className="button is-link is-small ml-5"
                    disabled={saving && 'disabled'}
                  >
                    {saving ? (
                      <>
                        Saving
                        <Loading size="is-size-6" />
                      </>
                    ) : ('Save')}
                  </button>
                </div>
              )}
            </footer>
          </form>
        </div>
      </div>
    )
  }
}
